import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Baby, Loader2 } from 'lucide-react';
import { api } from './api';
import { useSafeBack } from '@/utils/useSafeBack';

/**
 * First-run child profile setup. Captures name + DOB + gender (optional).
 * Once saved, the parent component (ChildrensHub) re-fetches the snapshot.
 */
const ChildSetup = ({ onCreated }) => {
  const navigate = useNavigate();
  // ChildSetup is rendered inline by ChildrensHub on the /childrens-health
  // route when no child profile exists. Falling back to /childrens-health
  // would loop the user back to this same screen, so the safe fallback is
  // the parent Family Health surface. Natural history pop still works.
  const handleBack = useSafeBack('/family-health');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const ten_years_ago = new Date(Date.now() - 10 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const canSave = name.trim().length >= 1 && /^\d{4}-\d{2}-\d{2}$/.test(dob);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError('');
    try {
      const created = await api.saveProfile({ name: name.trim(), dob, gender: gender || null });
      onCreated?.(created);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-white to-white pb-24" data-testid="child-setup">
      <div className="max-w-md mx-auto px-4 sm:px-6 py-6">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center">
            <Baby className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add your child</h1>
            <p className="text-xs text-gray-500 mt-0.5">A calm, age-aware health companion. One child to start.</p>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Child's name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Aria"
              maxLength={60}
              className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-sky-400"
              data-testid="setup-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Date of birth</label>
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              min={ten_years_ago}
              max={today}
              className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-sky-400"
              data-testid="setup-dob"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Gender (optional)</label>
            <div className="grid grid-cols-3 gap-2">
              {[['f', 'Female'], ['m', 'Male'], ['other', 'Other']].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setGender(gender === k ? '' : k)}
                  className={`text-sm font-medium rounded-xl border-2 px-3 py-2.5 transition-all ${
                    gender === k ? 'border-sky-500 bg-sky-50 text-sky-900' : 'border-gray-200 hover:border-sky-300 text-gray-700'
                  }`}
                  data-testid={`setup-gender-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}

        <button
          onClick={handleSave}
          disabled={!canSave || saving}
          className={`w-full mt-4 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
            canSave && !saving ? 'bg-gradient-to-br from-sky-500 to-blue-500 hover:brightness-110 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          data-testid="setup-save"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? 'Saving…' : "Save & continue"}
        </button>
      </div>
    </div>
  );
};

export default ChildSetup;
